import { Extension } from '@tiptap/core';
import type { ExtensionProps, Group, RicosExtension } from 'ricos-types';
import { unreachable } from 'wix-rich-content-editor-common';

import type { TiptapAdapterServices } from '../initializeTiptapAdapter';
import type {
  ExtensionAggregate,
  IExtension,
  IFunctionalExtension,
  IHtmlNodeExtension,
  IMarkExtension,
  IReactNodeExtension,
} from './domain-types';
import { FunctionalExtension } from './FunctionalExtension';
import { FunctionalExtensions } from './FunctionalExtensions';
import { IExtensionAggregate } from './IExtensionAggregate';
import { MarkExtension } from './MarkExtension';
import { MarkExtensions } from './MarkExtensions';
import { HtmlNodeExtension, ReactNodeExtension } from './NodeExtension';
import { HtmlNodeExtensions, ReactNodeExtensions } from './NodeExtensions';

// TODO: default extension should be FunctionalExtension with empty functionality
const defaultIExtension = {
  toTiptapExtension: Extension.create,
};

const toIExtension = (ext: RicosExtension): IExtension => {
  switch (ext.type) {
    case 'react-node':
      return new ReactNodeExtension(ext);
    case 'html-node':
      return new HtmlNodeExtension(ext);
    case 'extension':
      return new FunctionalExtension(ext);
    case 'mark':
      return new MarkExtension(ext);
    default:
      unreachable(ext);
      return defaultIExtension as unknown as IExtension;
  }
};

const validate = (extensions: IExtension[]) => {
  const names = extensions.map(e => e.name);
  const duplicateNames = names
    .map(name => ({ name, duplicate: names.indexOf(name) !== names.lastIndexOf(name) }))
    .filter(({ duplicate }) => !!duplicate)
    .map(({ name }) => name);
  if (duplicateNames.length > 0) {
    throw new NameCollisionError(`Extension name collision: ${duplicateNames.join(' ,')}`);
  }
};

class NameCollisionError extends Error {}

/**
 * Extensions is an aggregate over Extension entities.
 * It provides filtering and sorting functionalities which are relevant only in context of multiple Extension instances.
 * The specific Extensions subtypes are NodeExtensions, MarkExtensions and FunctionalExtensions.
 * They rely on filtering and constructed internally by the Extensions.
 */
export class Extensions implements ExtensionAggregate {
  private extensions: IExtensionAggregate<IExtension>;

  private readonly ricosProps: ExtensionProps;

  private readonly services: TiptapAdapterServices;

  private constructor(
    extensions: IExtension[],
    props: ExtensionProps,
    services: TiptapAdapterServices
  ) {
    this.extensions = new IExtensionAggregate<IExtension>(extensions);
    this.ricosProps = props;
    this.services = services;
  }

  static of(extensions: RicosExtension[], props: ExtensionProps, services: TiptapAdapterServices) {
    const iExtensions = extensions.map(toIExtension);
    validate(iExtensions);
    return new Extensions(iExtensions, props, services);
  }

  getRicosExtensions() {
    return this.extensions.asArray().map(ext => ext.getRicosExtension());
  }

  getFunctionalExtensions() {
    return new FunctionalExtensions(
      this.extensions.filter(ext => ext.type === 'extension').asArray() as IFunctionalExtension[]
    );
  }

  getReactNodeExtensions() {
    return new ReactNodeExtensions(
      this.extensions.filter(ext => ext.type === 'react-node').asArray() as IReactNodeExtension[]
    );
  }

  getHtmlNodeExtensions() {
    return new HtmlNodeExtensions(
      this.extensions.filter(ext => ext.type === 'html-node').asArray() as IHtmlNodeExtension[]
    );
  }

  getMarkExtensions() {
    return new MarkExtensions(
      this.extensions.filter(ext => ext.type === 'mark').asArray() as IMarkExtension[]
    );
  }

  getDecoratedNodeExtensions() {
    const hocComposer = this.getFunctionalExtensions().getNodeHocComposer(
      this,
      this.ricosProps,
      this.services
    );
    return this.getReactNodeExtensions().getDecoratedNodeExtensions(hocComposer);
  }

  getTiptapExtensions() {
    const reactNodes = this.getDecoratedNodeExtensions().toTiptapExtensions(
      this,
      this.ricosProps,
      this.services
    );
    const htmlNodes = this.getHtmlNodeExtensions().toTiptapExtensions(
      this,
      this.ricosProps,
      this.services
    );
    const marks = this.getMarkExtensions().toTiptapExtensions(this, this.ricosProps, this.services);
    const extensions = this.getFunctionalExtensions().toTiptapExtensions(
      this,
      this.ricosProps,
      this.services
    );
    return [...extensions, ...marks, ...reactNodes, ...htmlNodes];
  }

  asArray() {
    return this.extensions.asArray();
  }

  concat(extensions: RicosExtension[]): Extensions {
    const iExtensions = extensions.map(toIExtension);
    validate(iExtensions);
    return new Extensions(
      this.extensions.asArray().concat(iExtensions),
      this.ricosProps,
      this.services
    );
  }

  byGroup(group: Group) {
    return new Extensions(
      this.extensions.filter(ext => ext.groups.includes(group)).asArray(),
      this.ricosProps,
      this.services
    );
  }
}
